import platform from "platform";

export class MiscLib {
    private pad2(n: number): string {
        return n < 10 ? `0${n}` : `${n}`;
    }

    /**
     * Formats a timestamp as "M:MMA/P" if today,
     * "MM/DD M:MMA/P" if this year,
     * "YYYY/MM/DD M:MMA/P" otherwise.
     */
    public formatTimestamp(timestamp: number): string {
        const date = new Date(timestamp);
        const now = new Date();

        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const day = date.getDate();

        const nowYear = now.getFullYear();
        const nowMonth = now.getMonth() + 1;
        const nowDay = now.getDate();

        const hours24 = date.getHours();
        const minutes = date.getMinutes();
        const suffix = hours24 >= 12 ? "P" : "A";
        let hours12 = hours24 % 12;
        if (hours12 === 0) hours12 = 12;
        const timeStr = `${hours12}:${this.pad2(minutes)}${suffix}`;

        const dateStrThisYear = `${this.pad2(month)}/${this.pad2(day)}`;
        const dateStrOtherYear = `${year}/${this.pad2(month)}/${this.pad2(day)}`;

        const isToday = year === nowYear && month === nowMonth && day === nowDay;

        if (isToday) {
            return timeStr;
        }

        if (year === nowYear) {
            return `${dateStrThisYear} ${timeStr}`;
        }

        return `${dateStrOtherYear} ${timeStr}`;
    }

    /**
     * Returns the time portion of a timestamp in the same format as formatTimestamp,
     * but without any date.
     * e.g. "3:05P"
     */
    public formatTime(timestamp: number): string {
        const date = new Date(timestamp);
        const hours24 = date.getHours();
        const minutes = date.getMinutes();
        const suffix = hours24 >= 12 ? "P" : "A";
        let hours12 = hours24 % 12;
        if (hours12 === 0) hours12 = 12;
        return `${hours12}:${this.pad2(minutes)}${suffix}`;
    }

    /**
     * Returns a relative date label:
     * - "Today" if timestamp is today
     * - "Yesterday" if timestamp was yesterday
     * - Day name (e.g. "Monday") if within the last 7 days
     * - "MM/DD" if in this year but older than 7 days
     * - "YYYY/MM/DD" if in a previous year
     */
    public getRelativeDate(timestamp: number): string {
        const date = new Date(timestamp);
        const now = new Date();

        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const day = date.getDate();

        const nowYear = now.getFullYear();
        const nowMonth = now.getMonth() + 1;
        const nowDay = now.getDate();

        // Today
        if (year === nowYear && month === nowMonth && day === nowDay) {
            return "Today";
        }

        // Yesterday
        const yesterday = new Date(now);
        yesterday.setDate(now.getDate() - 1);
        if (
            year === yesterday.getFullYear() &&
            month === yesterday.getMonth() + 1 &&
            day === yesterday.getDate()
        ) {
            return "Yesterday";
        }

        // Within last 7 days (but not today/yesterday)
        const diffMs = now.getTime() - date.getTime();
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        if (diffDays < 7) {
            const weekdays = [
                "Sunday",
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday",
                "Saturday",
            ];
            return weekdays[date.getDay()];
        }

        // Older dates
        const dateStrThisYear = `${this.pad2(month)}/${this.pad2(day)}`;
        if (year === nowYear) {
            return dateStrThisYear;
        }

        const dateStrOtherYear = `${year}/${this.pad2(month)}/${this.pad2(day)}`;
        return dateStrOtherYear;
    }

    /**
     * Simple async wait
     */
    public async wait(milliseconds: number): Promise<void> {
        return new Promise((resolve) => setTimeout(resolve, milliseconds));
    }

    /**
     * Returns true if two timestamps are on different days.
     */
    public isDifferentDay(timestamp1: number, timestamp2: number): boolean {
        const date1 = new Date(timestamp1);
        const date2 = new Date(timestamp2);
        return (
            date1.getFullYear() !== date2.getFullYear() ||
            date1.getMonth() !== date2.getMonth() ||
            date1.getDate() !== date2.getDate()
        );
    }

    /**
   * Returns the distance in pixels from `element` up to (but not including)
   * the given `ancestor` by summing offsetTop up the offsetParent chain.
   */
    public getRelativeTop(element: HTMLElement, ancestor: HTMLElement): number {
        let top = 0;
        let el: HTMLElement | null = element;

        while (el && el !== ancestor) {
            top += el.offsetTop;
            el = el.offsetParent as HTMLElement | null;
        }

        return top;
    }

    public safeJsonParse<T = any>(value: string, fallback: T | null = null): T | null {
        try {
            return JSON.parse(value) as T;
        }
        catch (err) {
            return fallback;
        }
    }

    public generateRandomString(length: number = 16): string {
        const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
        let result = "";
        for (let i = 0; i < length; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return result;
    }

    public get isMac() {
        const osFamily = (platform.os?.family || "").toLowerCase();
        return osFamily.includes("mac") || osFamily.includes("os x");
    }

    public extractExtensionFromUrl(url: string) {
        const lastSegment = url.split("/").pop() || "";
        const parts = lastSegment.split(".");
        return parts.length > 1 ? (parts.pop() || "").toLowerCase() : null;
    }

    public async getImageAspectRatio(blob: Blob) {
        return new Promise<number>((resolve) => {
            const img = new Image();
            const url = URL.createObjectURL(blob);

            img.onload = () => {
                if (img.width > 0) {
                    const aspectRatio = img.height / img.width;
                    resolve(aspectRatio);
                }
                else {
                    resolve(-1);
                }
                URL.revokeObjectURL(url);
            };

            img.onerror = (err) => {
                URL.revokeObjectURL(url);
                resolve(-1);
            };

            img.src = url;
        });
    }

    downloadBlobUrl(url: string, filename: string) {
        const a = document.createElement("a");
        a.style.position = "absolute";
        a.style.visibility = "hidden";
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a)
    }

    private djb2Hash(str: string): number {
        let hash = 5381;
        for (let i = 0; i < str.length; i++) {
            hash = (hash * 33) ^ str.charCodeAt(i);
        }
        return hash >>> 0; // Ensure positive unsigned 32-bit integer
    }

    public generateFixedLengthHex(str: string, length: number = 16): string {
        let combinedHash = "";
        for (let i = 0; combinedHash.length < length; i++) {
            combinedHash += this.djb2Hash(str + String(i)).toString(16);
        }
        const paddingChar = "0";
        while (combinedHash.length < length) {
            combinedHash += paddingChar;
        }
        return combinedHash.substring(0, length);
    }
}

export const libMisc = new MiscLib();